import React, { useEffect, useMemo, useState, useRef, Fragment } from 'react';
import { RowData, Inputs } from '../utils/roiCalculations';
import { fmtMoney } from '../utils/formatters';
interface ProjectionTableProps {
  rows: RowData[];
  inputs: Inputs;
  onInputsChange: (inputs: Inputs) => void;
  halfSales: boolean;
  setHalfSales: (val: boolean) => void;
  doubleAttrition: boolean;
  setDoubleAttrition: (val: boolean) => void;
}
export function ProjectionTable({
  rows,
  inputs,
  onInputsChange,
  halfSales,
  setHalfSales,
  doubleAttrition,
  setDoubleAttrition
}: ProjectionTableProps) {
  const [tooltip, setTooltip] = useState<{
    content: string;
    x: number;
    y: number;
  } | null>(null);
  const [editingAdSpend, setEditingAdSpend] = useState<number | null>(null);
  const [editAdSpendValue, setEditAdSpendValue] = useState<string>('');
  const [timeframe, setTimeframe] = useState<3 | 6 | 9 | 12>(6);
  const [expandedSections, setExpandedSections] = useState({
    recruiting: true,
    activeReps: true,
    investment: true,
    revenue: true
  });
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const checkScrollButtons = () => {
    if (scrollContainerRef.current) {
      const {
        scrollLeft,
        scrollWidth,
        clientWidth
      } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };
  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300;
      const newScrollLeft = scrollContainerRef.current.scrollLeft + (direction === 'right' ? scrollAmount : -scrollAmount);
      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
      setTimeout(checkScrollButtons, 300);
    }
  };
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  const showTooltip = (content: string, e: React.MouseEvent) => {
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    setTooltip({
      content,
      x: rect.right + 8,
      y: rect.top
    });
  };
  const hideTooltip = () => {
    setTooltip(null);
  };
  const handleAdSpendChange = (monthIndex: number, value: number) => {
    if (monthIndex === 0) return;
    const arrayIndex = monthIndex - 1;
    const newMonthlyAdSpend = [...(inputs.monthlyAdSpend || Array(12).fill(inputs.adSpend))];
    newMonthlyAdSpend[arrayIndex] = Math.max(0, value);
    onInputsChange({
      ...inputs,
      monthlyAdSpend: newMonthlyAdSpend
    });
  };
  const handlePushAdSpendRight = (monthIndex: number) => {
    if (monthIndex === 0) return;
    const arrayIndex = monthIndex - 1;
    const currentMonthlyAdSpend = inputs.monthlyAdSpend || Array(12).fill(inputs.adSpend);
    const valueToPropagate = currentMonthlyAdSpend[arrayIndex];
    const newMonthlyAdSpend = currentMonthlyAdSpend.map((val, idx) => {
      return idx >= arrayIndex ? valueToPropagate : val;
    });
    onInputsChange({
      ...inputs,
      monthlyAdSpend: newMonthlyAdSpend
    });
  };
  const startEditingAdSpend = (monthIndex: number, currentValue: number) => {
    if (monthIndex === 0) return;
    setEditingAdSpend(monthIndex);
    setEditAdSpendValue(currentValue.toString());
  };
  const saveAdSpendEdit = () => {
    if (editingAdSpend !== null) {
      const value = parseFloat(editAdSpendValue) || 0;
      handleAdSpendChange(editingAdSpend, value);
      setEditingAdSpend(null);
      setEditAdSpendValue('');
    }
  };
  const cancelAdSpendEdit = () => {
    setEditingAdSpend(null);
    setEditAdSpendValue('');
  };
  const handleAdSpendKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveAdSpendEdit();
    } else if (e.key === 'Escape') {
      cancelAdSpendEdit();
    }
  };
  const adjustedRows = useMemo(() => {
    if (!halfSales && !doubleAttrition) return rows;
    return rows.map(row => {
      let newRow = {
        ...row
      };
      if (halfSales) {
        newRow.totalSales = row.totalSales * 0.5;
        newRow.grossProfit = row.grossProfit * 0.5;
        newRow.grossRevenue = row.grossRevenue * 0.5;
      }
      if (doubleAttrition) {
        newRow.launched = row.launched * 0.5;
        newRow.active30d = row.active30d * 0.5;
        newRow.active60d = row.active60d * 0.5;
        newRow.active90d = row.active90d * 0.5;
        newRow.veterans = row.veterans * 0.5;
        const retentionMultiplier = 0.5;
        const salesMultiplier = halfSales ? 0.5 : 1;
        newRow.totalSales = row.totalSales * retentionMultiplier * salesMultiplier;
        newRow.grossProfit = row.grossProfit * retentionMultiplier * salesMultiplier;
        newRow.grossRevenue = row.grossRevenue * retentionMultiplier * salesMultiplier;
      }
      newRow.netProfit = newRow.grossProfit - row.totalCost;
      return newRow;
    });
  }, [rows, halfSales, doubleAttrition]);
  const finalRows = useMemo(() => {
    let cumProfit = adjustedRows[0].cumProfit;
    return adjustedRows.map((row, idx) => {
      if (idx === 0) return row;
      cumProfit += row.netProfit;
      return {
        ...row,
        cumProfit
      };
    });
  }, [adjustedRows]);
  const displayRows = useMemo(() => {
    // Skip the Launch row (index 0) and show only the selected number of months
    return finalRows.slice(1, timeframe + 1);
  }, [finalRows, timeframe]);
  useEffect(() => {
    checkScrollButtons();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollButtons);
      window.addEventListener('resize', checkScrollButtons);
      return () => {
        container.removeEventListener('scroll', checkScrollButtons);
        window.removeEventListener('resize', checkScrollButtons);
      };
    }
  }, [timeframe]);
  const getFormula = (metricKey: string, row: RowData, rowIndex: number): string => {
    const monthNum = rowIndex + 1;
    switch (metricKey) {
      case 'hires':
        return `Formula: Ad Spend ÷ Cost per Hire\n\nActual: ${fmtMoney(row.adSpend)} ÷ ${fmtMoney(inputs.cph)} = ${row.hires.toFixed(0)} hires`;
      case 'launched':
        const launchRate = row.hires > 0 ? (row.launched / row.hires * 100).toFixed(0) : 0;
        return `Formula: Hires × Launch %\n\nActual: ${row.hires.toFixed(0)} × ${launchRate}% = ${row.launched.toFixed(1)} launched`;
      case 'active30d':
        if (monthNum === 1) return 'Month 1 - reps not yet at 30 days';
        return `Reps who are 30-60 days old: ${row.active30d.toFixed(1)}`;
      case 'active60d':
        if (monthNum <= 2) return 'Not enough time elapsed (need 60 days)';
        return `Reps who are 60-90 days old: ${row.active60d.toFixed(1)}`;
      case 'active90d':
        if (monthNum < 4) return 'Building cohort (need 90 days)...';
        return `Reps who are 90+ days old: ${row.active90d.toFixed(1)}`;
      case 'veterans':
        if (monthNum <= 4) return 'No veterans yet (need 120+ days)';
        return `Veteran reps (120+ days): ${row.veterans.toFixed(1)}`;
      case 'teamSize':
        const ts_launched = row.launched * 0.5;
        const ts_30to60 = row.active30d;
        const ts_60to90 = row.active60d;
        const ts_90plus = monthNum === 4 ? row.active90d : row.active90d + row.veterans;
        return `Total Team Size Breakdown:\n• Launched (0-30d): ${ts_launched.toFixed(1)}\n• Active 30-60d: ${ts_30to60.toFixed(1)}\n• Active 60-90d: ${ts_60to90.toFixed(1)}\n• Active 90d+: ${ts_90plus.toFixed(1)}\n\nTotal: ${(ts_launched + ts_30to60 + ts_60to90 + ts_90plus).toFixed(1)}`;
      case 'adSpend':
        return `Monthly ad spend investment\n\nActual: ${fmtMoney(row.adSpend)}`;
      case 'recruitingTimeCost':
        const hires = row.hires;
        const timeCost = row.hireCost;
        const hourlyRate = inputs.recruiterHourlyRate;
        return `Formula: Hires × 5 hours × Hourly Rate\n\nActual: ${hires.toFixed(0)} hires × 5 hours × ${fmtMoney(hourlyRate)}/hour\n= ${fmtMoney(timeCost)}`;
      case 'totalCost':
        return `Total monthly investment\n\nAd Spend: ${fmtMoney(row.adSpend)}\nRecruiting Time: ${fmtMoney(row.hireCost)}\nTotal: ${fmtMoney(row.totalCost)}`;
      case 'totalSales':
        const salesNote = halfSales ? '\n\n⚠️ 50% Sales Scenario active' : '';
        const attrNote = doubleAttrition ? '\n⚠️ 2x Attrition Scenario active' : '';
        return `Total sales from all active reps\n\nActual: ${row.totalSales.toFixed(1)} sales${salesNote}${attrNote}`;
      case 'realizationRate':
        return `Deal close rate\n\nActual: ${(row.realizationRate * 100).toFixed(0)}% of sales close`;
      case 'grossRevenue':
        return `Formula: Total Sales × Close Rate × Avg Deal Size\n\nActual: ${row.totalSales.toFixed(1)} × ${(row.realizationRate * 100).toFixed(0)}% × ${fmtMoney(inputs.ticket)}\n= ${fmtMoney(row.grossRevenue)}`;
      case 'grossProfit':
        return `Formula: Gross Revenue × Margin %\n\nActual: ${fmtMoney(row.grossRevenue)} × ${inputs.gpm}%\n= ${fmtMoney(row.grossProfit)}`;
      case 'netProfit':
        return `Formula: Gross Profit - Total Investment\n\nActual: ${fmtMoney(row.grossProfit)} - ${fmtMoney(row.totalCost)}\n= ${fmtMoney(row.netProfit)}`;
      case 'cumProfit':
        return `Running total of net profit from Month 1 to ${row.period}\n\nActual: ${fmtMoney(row.cumProfit)}`;
      default:
        return 'Calculation details';
    }
  };
  const metricGroups = [{
    id: 'recruiting',
    label: 'Recruiting',
    color: 'bg-blue-50',
    collapsible: true,
    metrics: [{
      key: 'hires',
      label: 'Hired Reps',
      format: (v: number) => v.toLocaleString()
    }]
  }, {
    id: 'activeReps',
    label: 'Active Reps',
    color: 'bg-purple-50',
    collapsible: true,
    metrics: [{
      key: 'launched',
      label: 'Launched',
      format: (v: number) => v.toFixed(1)
    }, {
      key: 'active30d',
      label: 'Active 30d+',
      format: (v: number) => v.toFixed(1)
    }, {
      key: 'active60d',
      label: 'Active 60d+',
      format: (v: number) => v.toFixed(1)
    }, {
      key: 'active90d',
      label: 'Active 90d+',
      format: (v: number) => v.toFixed(1)
    }, {
      key: 'teamSize',
      label: 'Total Team Size',
      format: (v: number) => v.toFixed(1),
      bold: true,
      centered: true
    }]
  }, {
    id: 'investment',
    label: 'Monthly Investment',
    color: 'bg-red-50',
    collapsible: true,
    metrics: [{
      key: 'adSpend',
      label: 'Ad Spend',
      format: (v: number) => fmtMoney(v),
      clickToEdit: true
    }, {
      key: 'recruitingTimeCost',
      label: 'Recruiting Time Cost',
      format: (v: number) => fmtMoney(v)
    }, {
      key: 'totalCost',
      label: 'Total Investment',
      format: (v: number) => fmtMoney(v),
      bold: true
    }]
  }, {
    id: 'revenue',
    label: 'Revenue & Profit',
    color: 'bg-green-50',
    collapsible: true,
    metrics: [{
      key: 'totalSales',
      label: 'Total Sales',
      format: (v: number) => v.toFixed(1),
      bold: true
    }, {
      key: 'realizationRate',
      label: 'Close Rate',
      format: (v: number) => `${(v * 100).toFixed(0)}%`
    }, {
      key: 'grossRevenue',
      label: 'Gross Revenue',
      format: (v: number) => fmtMoney(v)
    }, {
      key: 'grossProfit',
      label: 'Gross Profit',
      format: (v: number) => fmtMoney(v)
    }, {
      key: 'netProfit',
      label: 'Net Profit',
      format: (v: number) => fmtMoney(v),
      highlight: true
    }, {
      key: 'cumProfit',
      label: 'Profit To Date',
      format: (v: number) => fmtMoney(v),
      highlight: true
    }]
  }];
  return <div className="bg-white border-2 border-gray-200 rounded-lg shadow-lg overflow-hidden" data-id="element-353">
      {/* Controls Header */}
      <div className="p-4 md:p-6 border-b-2 border-gray-100 bg-gray-50" data-id="element-354">
        <div className="flex flex-col gap-6" data-id="element-355">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-356">
            <h2 className="text-lg md:text-xl font-bold text-navy" data-id="element-357">
              {inputs.companyName ? `Monthly Projections for ${inputs.companyName}` : 'Monthly Projections'}
            </h2>

            {/* Timeframe Selector */}
            <div className="inline-flex bg-white rounded-lg border border-gray-200 p-1 shadow-sm self-start md:self-auto" data-id="element-358">
              {[3, 6, 9, 12].map(months => <button key={months} onClick={() => setTimeframe(months as 3 | 6 | 9 | 12)} className={`px-3 py-1.5 md:px-4 md:py-2 rounded-md text-sm font-medium transition-all ${timeframe === months ? 'bg-navy text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-359">
                  {months}M
                </button>)}
            </div>
          </div>

          {/* Scenario Toggles */}
          <div className="flex flex-col sm:flex-row gap-3" data-id="element-360">
            <button onClick={() => setHalfSales(!halfSales)} className={`flex-1 px-4 py-3 rounded-lg text-sm font-semibold transition-all border-2 flex items-center justify-center gap-2 ${halfSales ? 'bg-red-50 border-red-500 text-red-700' : 'bg-white border-gray-200 text-gray-600 hover:border-red-200'}`} data-id="element-361">
              <div className={`w-4 h-4 rounded border flex items-center justify-center ${halfSales ? 'bg-red-500 border-red-500 text-white' : 'border-gray-300'}`} data-id="element-362">
                {halfSales && <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-363">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-364" />
                  </svg>}
              </div>
              Stress Test: 50% Sales
            </button>
            <button onClick={() => setDoubleAttrition(!doubleAttrition)} className={`flex-1 px-4 py-3 rounded-lg text-sm font-semibold transition-all border-2 flex items-center justify-center gap-2 ${doubleAttrition ? 'bg-orange-50 border-orange-500 text-orange-700' : 'bg-white border-gray-200 text-gray-600 hover:border-orange-200'}`} data-id="element-365">
              <div className={`w-4 h-4 rounded border flex items-center justify-center ${doubleAttrition ? 'bg-orange-500 border-orange-500 text-white' : 'border-gray-300'}`} data-id="element-366">
                {doubleAttrition && <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-367">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-368" />
                  </svg>}
              </div>
              Stress Test: 2x Attrition
            </button>
          </div>
        </div>
      </div>

      {/* Table Container with Scroll Buttons */}
      <div className="relative" data-id="element-369">
        {/* Left Scroll Button */}
        {canScrollLeft && <button onClick={() => scroll('left')} className="absolute left-48 top-1/2 -translate-y-1/2 z-30 bg-navy text-white p-2 rounded-full shadow-lg hover:bg-navy-light transition-all" aria-label="Scroll left" data-id="element-370">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-371">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" data-id="element-372" />
            </svg>
          </button>}

        {/* Right Scroll Button */}
        {canScrollRight && <button onClick={() => scroll('right')} className="absolute right-4 top-1/2 -translate-y-1/2 z-30 bg-navy text-white p-2 rounded-full shadow-lg hover:bg-navy-light transition-all" aria-label="Scroll right" data-id="element-373">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-374">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" data-id="element-375" />
            </svg>
          </button>}

        <div ref={scrollContainerRef} className="overflow-x-auto" data-id="element-376">
          <table className="w-full border-collapse text-sm min-w-[800px]" data-id="element-377">
            <thead data-id="element-378">
              <tr className="bg-navy text-white" data-id="element-379">
                <th className="sticky left-0 z-20 bg-navy text-xs font-bold uppercase tracking-wide p-4 text-left w-48" data-id="element-380">
                  Metric
                </th>
                {displayRows.map(row => <th key={row.period} className="bg-navy text-xs font-bold uppercase tracking-wide p-4 text-center whitespace-nowrap min-w-[100px]" data-id="element-381">
                    {row.period}
                  </th>)}
              </tr>
            </thead>
            <tbody className="bg-white" data-id="element-382">
              {metricGroups.map(group => {
              const isExpanded = expandedSections[group.id as keyof typeof expandedSections];
              const showDetails = !group.collapsible || isExpanded;
              return <Fragment key={group.id} data-id="element-383">
                    {/* Section Header */}
                    <tr className={`${group.color} border-b border-gray-200`} data-id="element-384">
                      <td className={`sticky left-0 z-10 ${group.color} bg-white p-3 font-bold text-gray-900 w-48 text-sm cursor-pointer hover:brightness-95 transition-all`} onClick={() => toggleSection(group.id as keyof typeof expandedSections)} data-id="element-385">
                        <div className="flex items-center gap-2" data-id="element-386">
                          <span className="text-navy text-xs" data-id="element-387">
                            {isExpanded ? '▼' : '▶'}
                          </span>
                          <span className="uppercase tracking-wide text-xs" data-id="element-388">
                            {group.label}
                          </span>
                        </div>
                      </td>
                      {displayRows.map((row, rowIdx) => {
                    const isEvenColumn = rowIdx % 2 === 0;
                    const zebraClass = isEvenColumn ? '' : 'bg-black/5';
                    return <td key={row.period} className={`${group.color} ${zebraClass} p-3 border-b border-gray-200`} data-id="element-389" />;
                  })}
                    </tr>

                    {/* Metrics Rows */}
                    {showDetails && group.metrics.map((metric, metricIdx) => {
                  const isLastInGroup = metricIdx === group.metrics.length - 1;
                  const borderClass = isLastInGroup ? 'border-b border-gray-300' : 'border-b border-gray-100';
                  const isEvenRow = metricIdx % 2 === 0;
                  const rowBgClass = isEvenRow ? 'bg-white' : 'bg-gray-50/50';
                  return <tr key={metric.key} className={`hover:bg-gray-50 transition-colors h-12 ${rowBgClass}`} data-id="element-390">
                            <td className={`sticky left-0 z-10 bg-white p-3 pl-8 text-gray-700 ${metric.bold ? 'font-bold text-sm' : 'font-medium text-xs'} ${borderClass} w-48`} data-id="element-391">
                              {metric.label}
                            </td>
                            {displayRows.map((row, rowIdx) => {
                      let value: number;
                      if (metric.key === 'teamSize') {
                        const launched0to30 = row.launched * 0.5;
                        const active30to60 = row.active30d;
                        const active60to90 = row.active60d;
                        const active90plus = row.active90d + row.veterans;
                        value = launched0to30 + active30to60 + active60to90 + active90plus;
                      } else if (metric.key === 'recruitingTimeCost') {
                        value = row.hireCost;
                      } else {
                        value = row[metric.key as keyof RowData] as number;
                      }
                      const formatted = metric.format(value);
                      const isEvenColumn = rowIdx % 2 === 0;
                      const columnShade = isEvenColumn ? '' : 'bg-gray-50/30';
                      let cellClass = `p-3 text-center whitespace-nowrap ${borderClass} relative group min-w-[100px] text-xs ${rowBgClass} ${columnShade}`;
                      if (metric.highlight) {
                        cellClass += value >= 0 ? ' text-green-600 font-bold bg-green-50/30' : ' text-red-600 font-bold bg-red-50/30';
                      } else if (metric.bold) {
                        cellClass += ' font-bold text-gray-900';
                      } else {
                        cellClass += ' text-gray-600';
                      }
                      let displayValue: React.ReactNode = formatted;
                      // Handle editable ad spend
                      if (metric.key === 'adSpend' && metric.clickToEdit) {
                        const actualMonthIndex = rowIdx + 1;
                        if (editingAdSpend === actualMonthIndex) {
                          displayValue = <div className="flex justify-center items-center gap-1" data-id="element-392">
                                      <input type="number" min="0" step="100" value={editAdSpendValue} onChange={e => setEditAdSpendValue(e.target.value)} onKeyDown={handleAdSpendKeyDown} onBlur={saveAdSpendEdit} autoFocus className="w-20 text-center border-2 border-amber rounded px-1 py-0.5 text-xs focus:border-amber outline-none bg-white shadow-sm" data-id="element-393" />
                                      {rowIdx < displayRows.length - 1 && <button onClick={() => {
                              saveAdSpendEdit();
                              handlePushAdSpendRight(actualMonthIndex);
                            }} className="w-5 h-5 flex items-center justify-center transition-colors hover:bg-amber/10 rounded text-amber" title="Apply to all future months" data-id="element-394">
                                          →
                                        </button>}
                                    </div>;
                        } else {
                          displayValue = <div className="flex justify-center items-center gap-1 group/edit" data-id="element-395">
                                      <span onClick={() => startEditingAdSpend(actualMonthIndex, value)} className="cursor-pointer hover:text-amber hover:underline decoration-dotted underline-offset-2 transition-colors" data-id="element-396">
                                        {formatted}
                                      </span>
                                      {rowIdx < displayRows.length - 1 ? <button onClick={() => handlePushAdSpendRight(actualMonthIndex)} className="w-4 h-4 flex items-center justify-center text-amber opacity-0 group-hover/edit:opacity-100 transition-opacity" title="Apply to all future months" data-id="element-397">
                                          →
                                        </button> : <div className="w-4 h-4" data-id="element-398"></div>}
                                    </div>;
                        }
                      }
                      return <td key={row.period} className={cellClass} data-id="element-399">
                                  {metric.clickToEdit ? <>{displayValue}</> : <div className="flex items-center justify-center gap-1" data-id="element-400">
                                      <span data-id="element-401">{displayValue}</span>
                                      <button className="opacity-0 group-hover:opacity-100 transition-opacity w-4 h-4 rounded-full bg-gray-200 text-gray-600 text-[10px] flex items-center justify-center hover:bg-navy hover:text-white flex-shrink-0" onMouseEnter={e => showTooltip(getFormula(metric.key, row, rowIdx), e)} onMouseLeave={hideTooltip} onClick={e => {
                            e.stopPropagation();
                            if (tooltip) hideTooltip();else showTooltip(getFormula(metric.key, row, rowIdx), e);
                          }} data-id="element-402">
                                        ?
                                      </button>
                                    </div>}
                                </td>;
                    })}
                          </tr>;
                })}
                  </Fragment>;
            })}
            </tbody>
          </table>
        </div>
      </div>

      {tooltip && <div className="fixed z-50 bg-navy text-white p-3 rounded shadow-xl border border-white/20 max-w-xs text-xs whitespace-pre-line pointer-events-none" style={{
      left: tooltip.x,
      top: tooltip.y
    }} data-id="element-403">
          {tooltip.content}
        </div>}
    </div>;
}